import { useEffect, useState } from 'react';
import type { NaverNewsConfig } from '@cornerstone/shared';
import { api } from '../../client/api';
import { ContentSkeleton } from '../../components/skeleton/ContentSkeleton';
import { BoPage, Section, ErrorBanner, OkBanner, inputCls, btnPrimary, btnGhost, errMsg } from './ui';

// =============================================================================
// BO · 네이버 뉴스 API (2026-09-27)
//
// 운영사 전용. NAVER API HUB 뉴스 검색 키 한 쌍을 등록하면 모든 고객사의 "네이버 뉴스" 위젯이 같이 쓴다.
// 키는 서버에 암호화 저장되며 다시 표시되지 않는다(메일 비밀번호·AI API 키와 같은 규칙).
// 활성화를 꺼 두면 편집기에서 네이버 뉴스를 고를 수 없고, 이미 배치된 위젯은 안내 문구만 표시한다.
// =============================================================================

export function NaverNewsSettings(): JSX.Element {
  const [config, setConfig] = useState<NaverNewsConfig | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [testQuery, setTestQuery] = useState('뉴스');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  function apply(cfg: NaverNewsConfig): void {
    setConfig(cfg); setEnabled(cfg.enabled); setClientId(''); setClientSecret('');
  }

  async function load() {
    setLoading(true);
    try { apply(await api.boGetNaverNewsConfig()); setErr(null); }
    catch (e) { setErr(errMsg(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { void load(); }, []);

  const hasId = Boolean(clientId.trim()) || Boolean(config?.hasClientId);
  const hasSecret = Boolean(clientSecret.trim()) || Boolean(config?.hasClientSecret);
  const missingKey = enabled && (!hasId || !hasSecret);

  async function save() {
    setSaving(true); setErr(null); setOk(null);
    try {
      const saved = await api.boSetNaverNewsConfig({
        enabled,
        // 빈 입력이면 기존 키 유지(undefined).
        ...(clientId.trim() ? { clientId: clientId.trim() } : {}),
        ...(clientSecret.trim() ? { clientSecret: clientSecret.trim() } : {}),
      });
      apply(saved);
      setOk(saved.enabled ? '저장했습니다. 모든 고객사 편집기에서 네이버 뉴스 위젯을 쓸 수 있습니다.' : '저장했습니다. 비활성 상태라 편집기에서 네이버 뉴스를 고를 수 없습니다.');
    } catch (e) { setErr(errMsg(e)); }
    finally { setSaving(false); }
  }

  async function reset() {
    if (!window.confirm('등록된 네이버 뉴스 키를 삭제하고 비활성화할까요? 배치된 네이버 뉴스 위젯은 안내 문구만 표시합니다.')) return;
    setSaving(true); setErr(null); setOk(null);
    try { apply(await api.boResetNaverNewsConfig()); setOk('키를 삭제하고 비활성화했습니다.'); }
    catch (e) { setErr(errMsg(e)); }
    finally { setSaving(false); }
  }

  async function test() {
    setTesting(true); setErr(null); setOk(null);
    try {
      const result = await api.boTestNaverNews(testQuery.trim() || '뉴스');
      setOk(`연결 성공 · "${result.query}" 기사 ${result.count}건 수신${result.titles.length ? ` — ${result.titles.join(' / ')}` : ''}`);
    } catch (e) {
      const m = errMsg(e);
      // 프록시(Cloudflare) 오류 페이지 HTML이 오면 원문 대신 안내만 보여 준다.
      setErr(/<!doctype|<html|cloudflare|bad gateway|gateway time-?out/i.test(m) ? '서버가 제때 응답하지 않아 프록시가 오류 페이지를 돌려줬습니다. 잠시 후 다시 시도하세요.' : m);
    }
    finally { setTesting(false); }
  }

  const status = !config ? null
    : config.enabled ? <span className="text-green">활성 · 모든 고객사 사용 중</span>
      : config.hasClientId && config.hasClientSecret ? <span className="text-text-dim">키 등록됨 · 비활성</span>
        : <span className="text-text-dim">키 미등록</span>;

  return (
    <BoPage title="네이버 뉴스 API" actions={<button type="button" className={btnGhost} onClick={() => void load()} disabled={loading}>새로고침</button>}>
      <ErrorBanner message={err} />
      <OkBanner message={ok} />
      <Section title="네이버 뉴스 API">
        {loading && !config && !err ? <ContentSkeleton label="네이버 뉴스 설정을 불러오는 중…" variant="form" /> : (
        <div className="flex flex-col gap-4 max-w-[560px]">
          <p role="status" className="rounded border border-line bg-panel-2 p-3 text-[13px] text-text-dim">
            NAVER API HUB 뉴스 검색 키를 한 번 등록하면 모든 고객사의 "네이버 뉴스" 위젯이 같이 씁니다. 현재 상태: {status}
            {config?.updatedAt ? ` · ${new Date(config.updatedAt).toLocaleString('ko-KR')} 저장` : ''}
          </p>

          <label className="flex items-center gap-2 text-[14px]">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            네이버 뉴스 위젯 활성화
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[13px] text-text-dim">
              Client ID {config?.hasClientId && <span className="text-green">· 저장됨 (끝자리 {config.clientIdHint ?? '····'})</span>}
            </span>
            <input className={inputCls} value={clientId} autoComplete="off" spellCheck={false}
              placeholder={config?.hasClientId ? '변경하려면 새 값 입력 (비우면 유지)' : 'NAVER API HUB Client ID'}
              onChange={(e) => setClientId(e.target.value)} />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[13px] text-text-dim">
              Client Secret {config?.hasClientSecret && <span className="text-green">· 저장됨</span>}
            </span>
            <input className={inputCls} type="password" value={clientSecret} autoComplete="new-password"
              placeholder={config?.hasClientSecret ? '변경하려면 새 값 입력 (비우면 유지)' : 'NAVER API HUB Client Secret'}
              onChange={(e) => setClientSecret(e.target.value)} />
            {missingKey && <span className="text-[12px] text-red">활성화하려면 Client ID와 Client Secret이 모두 필요합니다.</span>}
          </label>

          <p className="text-[12px] text-text-faint">
            화면에는 결과를 네이버가 준 순서 그대로 표시하고 출처를 "네이버 검색결과"로 적습니다. 서버가 검색어마다 10분에 한 번만 호출합니다.
          </p>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button type="button" className={btnPrimary} onClick={() => void save()} disabled={saving || loading || missingKey}>
              {saving ? '저장 중…' : '저장'}
            </button>
            <button type="button" className={btnGhost} onClick={() => void reset()}
              disabled={saving || loading || !config || (!config.hasClientId && !config.hasClientSecret && !config.enabled)}>
              키 삭제
            </button>
          </div>

          <div className="flex flex-col gap-2 border-t border-line pt-4">
            <p className="text-[13px] text-text-dim">저장된 키로 뉴스 검색을 한 번 호출합니다. 저장하지 않은 입력은 쓰이지 않으니 먼저 저장하세요.</p>
            <div className="flex flex-wrap items-center gap-2">
              <input className={`${inputCls} flex-1 min-w-[200px]`} value={testQuery} maxLength={50} aria-label="테스트 검색어"
                onChange={(e) => setTestQuery(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void test(); }} />
              <button type="button" className={btnGhost} onClick={() => void test()}
                disabled={testing || saving || loading || !config?.hasClientId || !config?.hasClientSecret}>
                {testing ? '확인 중…' : '연결 테스트'}
              </button>
            </div>
          </div>
        </div>
        )}
      </Section>
    </BoPage>
  );
}
